import { TableRow, TableCell, IconButton, Box, Typography, Switch, Collapse, Table, TableBody } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import EditIcon from '@mui/icons-material/Edit';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AddIcon from '@mui/icons-material/Add';
import { useEffect, useState, useRef, useCallback } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import CustomCheckBoxV2 from '../../../../components/CheckBoxV2/CheckBox';
import * as styles from './FoodTable.styles';
import { Food, FoodItem, ITEM_TYPE, MenuType, RecommendedFoodMeta } from '../../userMenu.types';
import { DraggableRecommendation } from './DraggableRecommendation';
import { AddRecommendedModal } from './AddRecommendedModal';
import { useUserMenuContext } from '../../../../context/UserMenuContext';

interface DraggableRowProps {
  item: FoodItem;
  index: number;
  moveRow: (dragIndex: number, hoverIndex: number) => void;
  handleBannerChange: (id: string, checked: boolean) => void;
  handleActiveChange: (id: string, checked: boolean) => void;
  handleEdit: (id: string) => void;
  onRecommendationsChange: (foodId: string, recommendations: FoodItem[]) => void;
}

export function DraggableRow({
  item,
  index,
  moveRow,
  handleBannerChange,
  handleActiveChange,
  handleEdit,
  onRecommendationsChange,
}: DraggableRowProps) {
  const { getFoodsByCategoryId, getFoodById, activeCategoryId, activeMenuType } = useUserMenuContext();
  const isPreOrderTab = activeMenuType === MenuType.REGULAR_PRE_ORDERS || activeMenuType === MenuType.SPECIAL_PRE_ORDERS;
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [recommendations, setRecommendations] = useState<FoodItem[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const rowRef = useRef<HTMLTableRowElement>(null);

  const loadRecommendations = useCallback(() => {
    if (!activeCategoryId) {
      setRecommendations([]);
      setShowRecommendations(false);
      return;
    }
    const foods = getFoodsByCategoryId(activeCategoryId);
    const sourceFood = foods.find((f: Food) => f.id === item.id);
    if (!sourceFood) {
      setRecommendations([]);
      setShowRecommendations(false);
      return;
    }
    const recMeta: RecommendedFoodMeta[] = sourceFood?.recommended_food ?? [];

    const mapped: FoodItem[] = recMeta
      .map((meta) => {
        const target = getFoodById(meta.recommended_food_id);
        if (!target) return null;

        return {
          id: meta.recommended_food_id,
          name: target.name ?? target.food_name ?? '',
          store: target.sku ?? '',
          image: Array.isArray(target.image_url) ? target.image_url[0] : target.image_url,
          price: target.price,
          banner: false,
          enabled: meta.enabled,
          restaurantId: target.restaurantId || '',
          food_enabled: meta.food_enabled,
        } as FoodItem;
      })
      .filter((x): x is FoodItem => x !== null);

    mapped.sort((a, b) => {
      const ma = recMeta.find((m) => m.recommended_food_id === a.id)?.order ?? 0;
      const mb = recMeta.find((m) => m.recommended_food_id === b.id)?.order ?? 0;
      return ma - mb;
    });

    setRecommendations(mapped);
  }, [getFoodsByCategoryId, getFoodById, item.id, activeCategoryId]);

  useEffect(() => {
    loadRecommendations();
  }, [loadRecommendations]);

  useEffect(() => {
    const handleRestore = () => {
      loadRecommendations();
    };
    window.addEventListener('recommendations-restore', handleRestore);
    return () => {
      window.removeEventListener('recommendations-restore', handleRestore);
    };
  }, [loadRecommendations]);

  const moveRecommendation = (dragIndex: number, hoverIndex: number) => {
    const dragItem = recommendations[dragIndex];
    const newRecommendations = [...recommendations];
    newRecommendations.splice(dragIndex, 1);
    newRecommendations.splice(hoverIndex, 0, dragItem);
    setRecommendations(newRecommendations);
    onRecommendationsChange(item.id, newRecommendations);
  };

  const handleAddRecommendations = (items: FoodItem[]) => {
    setRecommendations(items);
    onRecommendationsChange(item.id, items);
  };

  const handleRecommendationActiveChange = (id: string, checked: boolean) => {
    const newRecommendations = recommendations.map((rec) => (rec.id === id ? { ...rec, enabled: checked } : rec));
    setRecommendations(newRecommendations);
    onRecommendationsChange(item.id, newRecommendations);
  };

  const handleRemoveRecommendation = (id: string) => {
    const newRecommendations = recommendations.filter((rec) => rec.id !== id);
    setRecommendations(newRecommendations);
    onRecommendationsChange(item.id, newRecommendations);
  };

  // Pre-order tab: only enable row when Pre-order switch is explicitly ON. Regular/Special: disable when Available is OFF.
  const isFoodDisabled = isPreOrderTab
    ? !item.enabled_pre_order
    : (item.food_enabled === false);

  const [{ isDragging }, drag] = useDrag({
    type: ITEM_TYPE,
    item: { index },
    canDrag: !isFoodDisabled,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    end: () => {
    },
  });

  const [, drop] = useDrop({
    accept: ITEM_TYPE,
    hover: (draggedItem: { index: number }, monitor) => {
      if (!rowRef.current) {
        return;
      }

      const dragIndex = draggedItem.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = rowRef.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();

      if (!clientOffset) {
        return;
      }

      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      moveRow(dragIndex, hoverIndex);
      draggedItem.index = hoverIndex;
    },
  });

  drag(drop(rowRef));

  return (
    <>
      <TableRow
        ref={rowRef}
        onClick={() => !isFoodDisabled && setShowRecommendations(!showRecommendations)}
        sx={{
          ...styles.tableRowStyles,
          bgcolor: isFoodDisabled ? '#F2F4F7' : !item.enabled ? '#f0f0f0' : 'inherit',
          opacity: isDragging ? 0.5 : 1,
          cursor: isFoodDisabled ? 'not-allowed' : isDragging ? 'grabbing' : 'pointer',
        }}
      >
        {/* Drag Handle Column */}
        <TableCell>
          <Box sx={styles.dragHandleBoxStyles}>
            <div style={{ display: 'flex', cursor: isFoodDisabled ? 'not-allowed' : 'grab' }}>
              <IconButton size="small" sx={styles.dragIconButtonStyles} disabled={isFoodDisabled}>
                <MenuIcon sx={styles.menuIconStyles} />
              </IconButton>
            </div>
          </Box>
        </TableCell>

        {/* Expand/Collapse Column */}
        <TableCell onClick={(e) => e.stopPropagation()}>
          <IconButton
            size="small"
            disabled={isFoodDisabled}
            onClick={() => {
              if (isFoodDisabled) return;
              if (showRecommendations) {
                setOpenModal(true);
              } else {
                setShowRecommendations(true);
              }
            }}
          >
            {showRecommendations ? <AddIcon sx={styles.expandIconStyles} /> : <AccountTreeIcon sx={styles.expandIconStyles} />}
          </IconButton>
        </TableCell>

        {/* Food Column - Image and Details */}
        <TableCell>
          <Box sx={styles.foodBoxStyles}>
            <Box component="img" src={item.image} alt={item.name} sx={styles.foodImageStyles} />
            <Box>
              <Typography sx={styles.foodNameStyles}>{item.name}</Typography>
              <Typography sx={styles.foodStoreStyles}>{item.store}</Typography>
            </Box>
          </Box>
        </TableCell>

        {/* Price Column */}
        <TableCell sx={styles.priceCellStyles}>
          <Box sx={styles.priceBoxStyles}>
            <Typography sx={styles.priceTypographyStyles}>¥{item.price.toLocaleString()}</Typography>
          </Box>
        </TableCell>

        {/* Banner Column - Numbered Badge */}
        <TableCell onClick={(e) => e.stopPropagation()} sx={{ textAlign: 'center' }}>
          {!isFoodDisabled && (
            <Box
              onClick={() => handleBannerChange(item.id, !item.banner)}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 20,
                height: 20,
                borderRadius: '4px',
                border: item.banner ? '1px solid #1570EF' : '1px solid #D0D5DD',
                backgroundColor: item.banner ? '#EFF8FF' : 'transparent',
                color: item.banner ? '#1570EF' : 'transparent',
                fontSize: 12,
                fontWeight: 700,
                cursor: 'pointer',
                userSelect: 'none',
                transition: 'all 0.15s',
                '&:hover': { borderColor: '#1570EF', opacity: 0.8 },
              }}
            >
              {item.banner ? (item.banner_order ?? '') : ''}
            </Box>
          )}
        </TableCell>

        {/* On/Off Column - Switch */}
        <TableCell onClick={(e) => e.stopPropagation()}>
          {!isFoodDisabled && <Switch checked={item.enabled} onChange={(e) => handleActiveChange(item.id, e.target.checked)} sx={styles.switchStyles} />}
        </TableCell>

        {/* Edit Column */}
        <TableCell onClick={(e) => e.stopPropagation()}>
          <IconButton size="small" disabled={isFoodDisabled} onClick={() => !isFoodDisabled && handleEdit(item.id)} sx={styles.editButtonStyles}>
            <EditIcon sx={styles.editIconStyles} />
          </IconButton>
        </TableCell>
      </TableRow>

      {/* Expandable Recommendations Row */}
      <TableRow>
        <TableCell colSpan={7} sx={styles.collapseCellStyles}>
          <Collapse in={showRecommendations} timeout="auto" unmountOnExit>
            <Table sx={styles.collapseTableStyles}>
              <colgroup>
                <col style={{ width: '9.4%' }} />
                <col style={{ width: '6.9%' }} />
                <col style={{ width: '38.8%' }} />
                <col style={{ width: '12.8%' }} />
                <col style={{ width: '11.7%' }} />
                <col style={{ width: '11%' }} />
                <col style={{ width: '9.4%' }} />
              </colgroup>
              <TableBody>
                {recommendations.map((recItem, recIndex) => (
                  <DraggableRecommendation
                    key={recItem.id}
                    recItem={recItem}
                    index={recIndex}
                    moveRecommendation={moveRecommendation}
                    onActiveChange={handleRecommendationActiveChange}
                    onRemove={handleRemoveRecommendation}
                  />
                ))}
              </TableBody>
            </Table>
          </Collapse>
        </TableCell>
      </TableRow>

      <AddRecommendedModal open={openModal} onClose={() => setOpenModal(false)} onAdd={handleAddRecommendations} currentRecommendations={recommendations} excludeId={item.id} />
    </>
  );
}
